import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { QRCodeSVG } from "qrcode.react";
import { useMeContext } from "../../MeApp";
import { useOnboardingForm } from "../useOnboardingForm";
import { useRequestSmsHandoff } from "../../meHooks";
import { Button, StepShell, StepHeading } from "@codel/dova-ds";
import { MAIN_FLOW_STEPS, type StepProps } from "../flowConfig";
import { COUPLE_EVENTS } from "../../analytics/events";
import { track } from "../../analytics/track";
import type { SmsHandoffResponse } from "../../types";

/** Tear-free media-query subscription with a synchronous initial value (no
 *  first-paint flash). SPA-only — the server snapshot is a no-op. */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", onChange);
        return () => mql.removeEventListener("change", onChange);
      }
      // Legacy Safari/iOS (<14): only addListener/removeListener exist.
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    },
    [query]
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false
  );
}

/** The glyph the old hand-styled anchor carried. Local: one call site, and
 *  putting an icon in the DS is its own decision. */
function ExternalLinkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

/** Matches Tailwind's `md` breakpoint (48rem / 768px). */
const DESKTOP_QUERY = "(min-width: 48rem)";

/**
 * S9 — SMS handoff. Mirrors Wren's production SMS-handoff screen
 * (`frontend/src/onboarding/steps/SignupConfirmation.tsx`):
 *   1. Request the deep link on mount → mock returns `accessCode` + `smsUri`.
 *   2. Mobile shows an iMessage-compose preview of the pre-filled message;
 *      desktop shows a QR encoding the same `sms:` URI to scan from a phone
 *      (an `sms:` link can't be tapped on a computer). The view follows the
 *      `md` breakpoint, with a manual link to switch as a backup for when
 *      device detection guesses wrong.
 *   3. "Text Dova" (footer, mobile) opens the native composer; the backend
 *      matches the inbound SMS to the partner via the code. On desktop the
 *      footer is a passive "waiting" status — the screen advances on its own
 *      once the text arrives.
 *
 * Plan C mocks the backend; the [DEV] banner advances without a real SMS.
 * Plan D replaces the mock + removes the dev banner (the screen will auto-
 * advance when the inbound SMS arrives).
 *
 * Sandbox port: the handoff request goes through the `useRequestSmsHandoff()`
 * hook (→ MSW `/me/partners/:id/sms-handoff`) instead of #938's injected
 * `apiClient`.
 */
export function S9HandoffStub({
  onNext,
  onBack,
  currentStepIndex,
  totalSteps,
}: StepProps) {
  const { partner } = useMeContext();
  const { markCompleted } = useOnboardingForm();
  const requestSmsHandoff = useRequestSmsHandoff(partner?.id ?? "");
  const [handoff, setHandoff] = useState<SmsHandoffResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The inbound SMS landing = onboarding done. Mark it, then advance straight
  // to the homebase (the standalone "You're in" success screen was dropped for
  // MVP). Fired in production by the couple-poll effect below; the [DEV] button
  // fires the same path manually.
  const handleSmsConfirmed = useCallback(() => {
    markCompleted();
    track(COUPLE_EVENTS.onboardingCompleted, { steps_total: MAIN_FLOW_STEPS.length });
    onNext();
  }, [markCompleted, onNext]);

  // ── Production advance ────────────────────────────────────────────────────
  // The acting partner's couple state is the source of truth for "their SMS
  // landed". `useMeCouple` polls `/me/couple` while any partner is still
  // onboarding (see meHooks.ts); each poll re-derives the acting partner's
  // `onboardingState.completedAt` from the backend `progress.completed`
  // (couple_resolution.derive_partner_progress: consent_present && has_one_on_one).
  // S8 consent precedes S9, so by this screen `consent_present` is already true
  // and `completed` flips precisely when `has_one_on_one` does — i.e. the moment
  // the partner's inbound SMS is matched and their coaching 1:1 is created
  // (couple_resolution._has_one_on_one). When that flips, advance S9→S11. This
  // is the ONLY production exit; the [DEV] button is stripped from prod builds.
  const actingCompleted = !!partner?.onboardingState.completedAt;
  const advancedRef = useRef(false);
  useEffect(() => {
    if (!actingCompleted || advancedRef.current) return;
    advancedRef.current = true;
    handleSmsConfirmed();
  }, [actingCompleted, handleSmsConfirmed]);

  // View follows the breakpoint by default; the manual switch overrides it.
  const prefersDesktop = useMediaQuery(DESKTOP_QUERY);
  const [viewOverride, setViewOverride] = useState<"mobile" | "desktop" | null>(
    null
  );
  const isDesktopView = viewOverride
    ? viewOverride === "desktop"
    : prefersDesktop;

  const requestMutate = requestSmsHandoff.mutateAsync;
  useEffect(() => {
    if (!partner) return;
    let cancelled = false;
    setHandoff(null);
    setError(null);
    void requestMutate()
      .then((resp) => {
        if (!cancelled) setHandoff(resp);
      })
      .catch((err) => {
        console.warn("[me] requestSmsHandoff rejected:", err);
        if (!cancelled) setError("The SMS link didn't load. Refresh to try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [partner, requestMutate]);

  // Render the EXACT message the backend prefilled into the `sms:` deep link
  // (`handoff.messageBody`, the same string encoded into `smsUri`) — so the preview the
  // client reads is what they actually send, and it carries the `DOVA-<code>` the inbound
  // handler parses. Never a separately-built string (which would drift from the real body).
  const messagePreview = handoff?.messageBody ?? null;

  return (
    <StepShell
      showBack
      onBack={onBack}
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      footer={
        isDesktopView ? (
          /* Desktop: nothing to tap — this screen advances on its own once
             Dova receives the text (Plan D) / the [DEV] banner fires (Plan C).
             A waiting status sized like the primary button keeps the footer
             balanced. */
          <div
            role="status"
            aria-live="polite"
            className="flex w-full items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[var(--border)] px-6 py-4 text-base font-semibold leading-none text-[var(--text-tertiary)]"
          >
            <span
              aria-hidden="true"
              className="h-2 w-2 animate-pulse rounded-full bg-[var(--text-tertiary)]"
            />
            Waiting for your text…
          </div>
        ) : (
          /* Mobile: tap-to-send opens the native composer on this device.
             A real link — sms: has to be handled by the OS, and this used to
             hand-restate Button's primary classes to look like one.

             Two branches rather than one disabled link. `href` and `disabled`
             are mutually exclusive on Button because an <a> has no disabled
             state: the old code faked it with pointer-events-none + opacity-50
             + preventDefault, which still left a focusable link a keyboard user
             could walk into and activate. Until the handoff arrives there is
             nowhere to go, so it is a disabled button. */
          handoff ? (
            <Button
              href={handoff.smsUri}
              target="_blank"
              rel="noopener noreferrer"
              size="xl"
              full
              trailingIcon={<ExternalLinkIcon />}
            >
              Text Dova
            </Button>
          ) : (
            <Button size="xl" full disabled trailingIcon={<ExternalLinkIcon />}>
              Text Dova
            </Button>
          )
        )
      }
    >
      {/*
        DEV-only advance — Plan D removes this (auto-advances on inbound SMS).
        Gated to dev builds so it never ships; `fixed` lifts it to a top bar.
      */}
      {import.meta.env.DEV && (
        <button
          type="button"
          onClick={handleSmsConfirmed}
          className="fixed inset-x-0 top-0 z-50 bg-[var(--text-primary)] py-2 text-center text-xs font-semibold text-white/90 transition-colors hover:text-white"
        >
          [DEV] Simulate inbound SMS
        </button>
      )}

      <StepHeading align="center" title="Say hi to Dova">
        <p className="max-w-[34ch]">
          {isDesktopView
            ? "Scan this QR code with your phone’s camera to text Dova and activate your account."
            : "Text Dova your code to turn on your coaching chat."}
        </p>
      </StepHeading>

      <div className="flex w-full flex-1 flex-col items-center justify-center gap-6 text-center">
        {error ? (
          <div className="w-full rounded-[var(--radius-md)] border border-[var(--danger)] bg-[var(--surface)] p-4 text-base text-[var(--danger)]">
            {error}
          </div>
        ) : !handoff || !messagePreview ? (
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-[var(--border)]" />
            <p className="text-base text-[var(--text-secondary)]">
              Preparing your link…
            </p>
          </div>
        ) : (
          <>
            {isDesktopView ? (
              /* QR — scan to text Dova from a phone (instructions live in the
                 heading description above). */
              <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-white p-4 shadow-[var(--shadow-card)]">
                <QRCodeSVG
                  value={handoff.smsUri}
                  size={176}
                  bgColor="#FFFFFF"
                  fgColor="#2A2E2B"
                  level="M"
                  role="img"
                  aria-label="QR code to text Dova from your phone"
                />
              </div>
            ) : (
              /* iMessage compose mockup — tap "Text Dova" to send. */
              <div className="w-full max-w-[19rem] overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] text-left shadow-[var(--shadow-card)]">
                <div className="border-b border-[var(--border)] px-4 py-2 text-center text-xs font-semibold text-[var(--text-primary)]">
                  New iMessage
                </div>
                <div className="border-b border-[var(--border)] px-4 py-2 text-xs text-[var(--text-secondary)]">
                  To: <span className="text-[#007AFF]">{handoff.dovaPhoneNumber}</span>
                </div>
                <div className="flex min-h-[112px] items-end justify-end px-4 pb-4 pt-8">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#007AFF] px-4 py-2 text-sm leading-snug text-white">
                    {messagePreview}
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col items-center gap-4">
              <p className="max-w-[42ch] text-xs leading-[1.5] text-[var(--text-tertiary)]">
                By continuing, you agree to receive text messages from Dova. Msg
                &amp; data rates may apply. Reply STOP anytime to opt out of
                non-essential messages.
              </p>
              {/* Manual switch — backup when the breakpoint picks the wrong
                  view (e.g. a phone in a wide viewport, or vice versa). */}
              <button
                type="button"
                onClick={() =>
                  setViewOverride(isDesktopView ? "mobile" : "desktop")
                }
                className="text-xs text-[var(--cta-ink)] underline underline-offset-2"
              >
                {isDesktopView ? "On your mobile?" : "On your computer?"}
              </button>
            </div>
          </>
        )}
      </div>
    </StepShell>
  );
}
